import { ReactNode } from 'react';
import {
  Box,
  Container,
  Flex,
  Heading,
  Progress,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { SetupStep } from '../../types';

interface SetupLayoutProps {
  children: ReactNode;
  currentStep: SetupStep;
  title: string;
  subtitle?: string;
  onSignOut?: () => void;
  userEmail?: string; // Add user email prop
}

const steps = [
  { title: 'Annual Target', description: 'Set your yearly target hours' },
  { title: 'Tracking Period', description: 'Define your tracking period' },
  { title: 'Work Week', description: 'Select your working days' },
  { title: 'Vacation Days', description: 'Mark your vacation days' },
];

const SetupLayout = ({ children, currentStep, title, subtitle, onSignOut, userEmail }: SetupLayoutProps) => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBgColor = useColorModeValue('white', 'gray.800');
  const linkColor = useColorModeValue('blue.500', 'blue.300');
  const userInfoBg = useColorModeValue('blue.50', 'blue.900');
  
  const progressValue = ((currentStep + 1) / steps.length) * 100;

  return (
    <Flex
      minH="100vh"
      direction="column"
      bg={bgColor}
      py={{ base: 4, md: 8 }}
      px={{ base: 3, md: 4 }}
    >
      <Container maxW={{ base: "100%", md: "4xl" }} mb={{ base: 4, md: 8 }}>
        <Box textAlign="center" mb={8}>
          <Heading as="h1" size="xl" mb={2}>
            Legal Billable Hours Tracker
          </Heading>
          <Text fontSize="lg" color="gray.600">
            First-time setup wizard
          </Text>
        </Box>

        {/* Display current user info */}
        {userEmail && (
          <Box 
            bg={userInfoBg} 
            p={3} 
            borderRadius="md" 
            mb={6}
            textAlign="center"
          >
            <Text fontSize="sm" fontWeight="medium">
              Current user: <Text as="span" fontWeight="bold">{userEmail}</Text>
            </Text>
          </Box>
        )}

        {/* Mobile view - simplified progress indicator */}
        <Box display={{ base: 'block', md: 'none' }} mb={6}>
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="medium">Step {currentStep + 1} of {steps.length}</Text>
            <Text fontWeight="bold">{steps[currentStep].title}</Text>
          </Flex>
          <Progress value={progressValue} size="sm" colorScheme="blue" borderRadius="md" />
        </Box>

        {/* Desktop view - full stepper */}
        <Box display={{ base: 'none', md: 'block' }} mb={8}>
          <Stepper index={currentStep}>
            {steps.map((step, index) => (
              <Step key={index}>
                <StepIndicator>
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>
                <Box flexShrink={0}>
                  <StepTitle>{step.title}</StepTitle>
                </Box>
                <StepSeparator />
              </Step>
            ))}
          </Stepper>
          <Progress value={progressValue} size="sm" colorScheme="blue" mt={4} borderRadius="md" />
        </Box>

        <Box
          bg={cardBgColor}
          rounded="lg"
          shadow="md"
          p={{ base: 4, md: 8 }}
        >
          <Heading as="h2" size={{ base: "md", md: "lg" }} mb={{ base: 1, md: 2 }}>
            {title}
          </Heading>
          {subtitle && (
            <Text fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={{ base: 4, md: 6 }}>
              {subtitle}
            </Text>
          )}
          
          {children}
        </Box>
        
        {/* Sign out link */}
        {onSignOut && (
          <Box textAlign="center" mt={4}>
            <Text 
              as="span" 
              color={linkColor}
              cursor="pointer"
              fontSize="sm"
              fontWeight="medium"
              _hover={{ textDecoration: 'underline' }}
              onClick={onSignOut}
            >
              Not you? Click here to sign out
            </Text>
          </Box>
        )}
      </Container>
    </Flex>
  );
};

export default SetupLayout;